"use client";

import useSWR from "swr";
import { collectionBrowseAction } from "@/actions/collection";
import { useStoreBuilderContext } from "@/ui/admin/store-builder/store-builder-context";
import { Label } from "@/ui/shadcn/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/ui/shadcn/select";
import { YnsLink } from "@/ui/yns-link";
import type { YnsComponentForm } from "../types";
import type { CollectionGrid } from "./component";
import type { CollectionGridStoreBuilderConfigFactory } from "./config";

export const CollectionGridForm: YnsComponentForm<typeof CollectionGridStoreBuilderConfigFactory> = ({
	data,
}) => {
	const { data: allCollections } = useSWR("getCollectionsAction", () => collectionBrowseAction(), {
		keepPreviousData: true,
	});

	const SHOW_COLLECTIONS = 3;

	// We always want to show SHOW_COLLECTIONS collections, even if `data.collections` is empty
	const collectionsWithPadding = (data.collections ?? []).concat(
		Array.from({ length: SHOW_COLLECTIONS - (data.collections?.length ?? 0) }, () => ({ slug: "" })),
	);

	const context = useStoreBuilderContext<typeof CollectionGrid>();
	return (
		<div className="grid gap-3">
			<p className="text-xs text-muted-foreground -mt-1">
				Collections group products together. You can define a collection on the{" "}
				<YnsLink href="/admin/collections" className="underline">
					Collections
				</YnsLink>{" "}
				page.
			</p>

			{collectionsWithPadding.map(({ id, slug }, idx) => {
				const matchedCollection = allCollections?.data?.find((c) => c.id === id || c.slug === slug);

				return (
					<Label key={idx + (matchedCollection?.id || "")} className="grid gap-1 mt-2">
						<span className="text-xs text-neutral-600 font-medium">Pick collection number {idx + 1}</span>
						<Select
							defaultValue={matchedCollection?.id ?? "__NULL__"}
							onValueChange={async (value) => {
								// save whole array in order to reset the default value
								// this is because postgres can't set value on non-existing object
								// this is a workaround
								await context.saveSelectedPageSectionField({
									fieldPath: `collections`,
									value: collectionsWithPadding,
								});
								await context.saveSelectedPageSectionField({
									fieldPath: `collections.${idx}.id`,
									value,
								});
							}}
							disabled={!allCollections?.data?.length}
						>
							<SelectTrigger className="w-full">
								<SelectValue placeholder="Select a collection" />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value={"__NULL__"}>(none)</SelectItem>
								{allCollections?.data?.map((collection) => (
									<SelectItem value={collection.id} key={collection.id}>
										{collection.name}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</Label>
				);
			})}
		</div>
	);
};
